/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import type { Order } from '@/components/account/order-list';
import type { CustomerOrdersPage, loader as accountOrdersLoader } from '@/routes/resource.account-orders';
import { resourceRoutes } from '@/route-paths';

/**
 * Narrow the raw `fetcher.data` (either our JSON payload or a bare error Response body) to a
 * {@link CustomerOrdersPage}, or `null` if it isn't one. A non-null return means the request succeeded.
 */
function asCustomerOrdersPage(data: unknown): CustomerOrdersPage | null {
    if (!data || typeof data !== 'object') {
        return null;
    }
    const candidate = data as Partial<CustomerOrdersPage>;
    if (
        !Array.isArray(candidate.orders) ||
        typeof candidate.nextOffset !== 'number' ||
        typeof candidate.hasMore !== 'boolean'
    ) {
        return null;
    }
    return candidate as CustomerOrdersPage;
}

/**
 * @property initialOrders - The first window of visible orders, server-rendered by the route loader.
 * @property initialNextOffset - Physical offset to resume from (the loader window's `nextOffset`).
 * @property initialHasMore - Whether the physical history extends past `initialNextOffset`.
 * @property limit - Chunk size to request per "View more" click.
 * @property total - Visible order count for the progress label, or `null` when it couldn't be derived
 *   (the failed-order count fetch failed). Also caps accumulation when known.
 */
export interface UseLoadMoreOrdersOptions {
    initialOrders: Order[];
    initialNextOffset: number;
    initialHasMore: boolean;
    limit: number;
    total: number | null;
}

/**
 * @property orders - All visible orders to render (initial window + every appended window).
 * @property loadedCount - How many orders are currently rendered.
 * @property total - Visible order count for the label, or `null` if unknown.
 * @property canLoadMore - Whether the "View more" button should be shown (more remain to load).
 * @property isLoading - Whether a "View more" request is in flight.
 * @property hasError - Whether the most recent "View more" request failed.
 * @property loadMore - Request the next window. No-op while loading or when nothing remains.
 */
export interface UseLoadMoreOrdersResult {
    orders: Order[];
    loadedCount: number;
    total: number | null;
    canLoadMore: boolean;
    isLoading: boolean;
    hasError: boolean;
    loadMore: () => void;
}

/**
 * Client-side accumulation for the order-history "View more" control.
 *
 * The first window of orders is server-rendered by the route loader. This hook appends further windows
 * by calling `/resource/account-orders` with `useFetcher` (a non-navigating GET), accumulating them
 * below the initial list. Each window carries a physical `nextOffset` cursor, so the server never
 * rescans from the top and hidden (failed) orders can't desync a page count — there are no page numbers.
 *
 * Accumulated windows reset when the initial window changes (e.g. the route loader revalidates after a
 * new order is placed), so the shopper never sees stale appended orders stacked on a refreshed list.
 */
export function useLoadMoreOrders({
    initialOrders,
    initialNextOffset,
    initialHasMore,
    limit,
    total,
}: UseLoadMoreOrdersOptions): UseLoadMoreOrdersResult {
    const fetcher = useFetcher<typeof accountOrdersLoader>();

    // Windows appended after the initial (server-rendered) one.
    const [appended, setAppended] = useState<Order[]>([]);
    // Physical offset to request next. Advances by each window's `nextOffset`.
    const nextOffsetRef = useRef<number>(initialNextOffset);
    // Whether the physical history extends past the cursor. Seeded from the loader, updated per window.
    const [hasMoreState, setHasMoreState] = useState<boolean>(initialHasMore);

    // Reset accumulation whenever the initial window's content changes (loader revalidation). Keyed on
    // the cursor + the order numbers so an identical refresh is a no-op, but a genuinely different first
    // page (e.g. a newly placed order) clears stale appended windows and re-seeds the cursor.
    const initialKey = `${initialNextOffset}:${initialHasMore}:${initialOrders.map((o) => o.orderNo).join(',')}`;
    const prevInitialKeyRef = useRef<string | null>(null);
    useEffect(() => {
        if (prevInitialKeyRef.current === initialKey) {
            return;
        }
        prevInitialKeyRef.current = initialKey;
        nextOffsetRef.current = initialNextOffset;
        setHasMoreState(initialHasMore);
        setAppended([]);
    }, [initialKey, initialNextOffset, initialHasMore]);

    // Merge each settled window into the accumulated list, advancing the cursor and de-duplicating by
    // order number (guards against an overlapping window ever rendering the same order twice).
    const result = asCustomerOrdersPage(fetcher.data);
    const lastMergedRef = useRef<unknown>(null);
    useEffect(() => {
        if (!result || fetcher.data === lastMergedRef.current) {
            return;
        }
        lastMergedRef.current = fetcher.data;

        // Advance the physical cursor and refresh "more remains" even when the window is empty or fully
        // de-duplicated, so a subsequent click resumes forward rather than re-requesting the same offset.
        nextOffsetRef.current = result.nextOffset;
        setHasMoreState(result.hasMore);

        const batch = result.orders;
        if (batch.length === 0) {
            return;
        }
        setAppended((prev) => {
            const seen = new Set<string>([...initialOrders.map((o) => o.orderNo), ...prev.map((o) => o.orderNo)]);
            const fresh = batch.filter((o) => !seen.has(o.orderNo));
            if (fresh.length === 0) {
                return prev;
            }
            return [...prev, ...fresh];
        });
    }, [fetcher.data, result, initialOrders]);

    const orders = appended.length === 0 ? initialOrders : [...initialOrders, ...appended];
    const loadedCount = orders.length;
    const isLoading = fetcher.state === 'loading';
    // A settled fetch whose payload isn't our JSON result (the resource route returned a non-2xx
    // Response, surfaced by useFetcher as the raw body) means the last request failed.
    const hasError = fetcher.state === 'idle' && fetcher.data !== undefined && result === null;
    // Offer more while the physical history has more AND (when known) we haven't shown the whole
    // visible set. The physical-cursor check is authoritative; `total` only trims a trailing all-hidden
    // tail so the button doesn't linger after the last visible order.
    const canLoadMore = hasMoreState && (total == null || loadedCount < total);

    const loadMore = useCallback(() => {
        if (isLoading || !canLoadMore) {
            return;
        }
        const params = new URLSearchParams({
            offset: String(nextOffsetRef.current),
            limit: String(limit),
        });
        void fetcher.load(`${resourceRoutes.accountOrders}?${params.toString()}`);
    }, [isLoading, canLoadMore, limit]); // oxlint-disable-line react-hooks/exhaustive-deps -- fetcher.load is stable per React Router

    return {
        orders,
        loadedCount,
        total,
        canLoadMore,
        isLoading,
        hasError,
        loadMore,
    };
}
